// Invitations in the browser. The one-time token travels in the address fragment (`#/invite/<token>`), which a
// browser never sends to a server, so it cannot end up in an access log. The page reads it once, keeps it in
// memory and in this tab's sessionStorage (so signing in does not lose it), and takes it out of the address bar.
// Everything here is pure or takes its browser pieces as arguments, so the state logic can be tested.
import type { Api } from "./api.ts";
import type { Role } from "./types.ts";

/** What `POST /invitations/preview` says. */
export interface InvitePreview {
  workspace_id: string;
  workspace_name: string;
  role: Role;
  invited_by_name: string | null;
  expires_at: string;
  used: boolean;
  /** null when the signed-in user has no email to compare */
  email_matches: boolean | null;
  already_member: boolean;
}

export type InvitePreviewResponse = InvitePreview | { state: "mismatch" | "used" | "expired" };

export interface InviteAccepted {
  workspace_id: string;
  role: Role;
  invitation_id: string;
}

/** Tokens are `fsi_` + base64url; accept anything URL-safe the server could have made. */
const TOKEN = /^[A-Za-z0-9_-]{10,200}$/;

export const isInviteToken = (s: string | null | undefined): s is string => !!s && TOKEN.test(s);

/** The token of an address like `#/invite/<token>`; null for any other address (and for `#/invite`). */
export function inviteTokenFromHash(hash: string): string | null {
  const parts = hash.replace(/^#\/?/, "").split("/");
  if (parts[0] !== "invite" || parts.length !== 2) return null;
  let t = parts[1] ?? "";
  try {
    t = decodeURIComponent(t);
  } catch {
    return null;
  }
  return isInviteToken(t) ? t : null;
}

/** The link a producer sends: the token sits after the `#`, so no server sees it. */
export function inviteLink(base: string, token: string): string {
  return `${base.split("#")[0]}#/invite/${token}`;
}

// ── the pending token ──────────────────────────────────────────────────────────

const STORE_KEY = "filmsquare.invite";

export interface TokenStore {
  get(): string | null;
  set(token: string | null): void;
  /** counts every token that is stored (not the clearing), so a screen starts over when another invitation link is opened */
  version(): number;
}

/** In memory first; sessionStorage as a backup so a sign-in round trip in this tab keeps it. */
export function createTokenStore(
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null,
): TokenStore {
  let mem: string | null = null;
  let changes = 0;
  return {
    version: () => changes,
    get() {
      if (mem) return mem;
      try {
        const v = storage?.getItem(STORE_KEY) ?? null;
        if (isInviteToken(v)) mem = v;
      } catch {
        // storage blocked: memory only
      }
      return mem;
    },
    set(token) {
      mem = token;
      if (token) changes++;
      try {
        if (token) storage?.setItem(STORE_KEY, token);
        else storage?.removeItem(STORE_KEY);
      } catch {
        // storage blocked: memory only
      }
    },
  };
}

function browserStorage(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** The app-wide store. */
export const pendingInvite: TokenStore = createTokenStore(browserStorage());

export interface HashEnv {
  location: Pick<Location, "hash" | "pathname" | "search">;
  history: Pick<History, "replaceState" | "state">;
}

/**
 * If the address carries an invitation token: remember it and take it out of the address bar (the entry is
 * replaced, so Back does not return to the token). Returns true when a token was captured.
 */
export function captureInviteFromHash(env: HashEnv = window, store: TokenStore = pendingInvite): boolean {
  const token = inviteTokenFromHash(env.location.hash);
  if (!token) return false;
  store.set(token);
  env.history.replaceState(env.history.state, "", `${env.location.pathname}${env.location.search}#/invite`);
  return true;
}

// ── what the screen shows ──────────────────────────────────────────────────────

/** One state of the accept screen. The token itself is never part of it. */
export type InviteView =
  | { s: "none" }
  | { s: "loading" }
  | { s: "ready"; p: InvitePreview }
  | { s: "joining"; p: InvitePreview }
  | { s: "used" }
  | { s: "expired" }
  | { s: "invalid" }
  /** the invitation is for another email address than the one signed in */
  | { s: "mismatch" }
  /** signed in with an API key: invitations need an email sign-in */
  | { s: "apikey" }
  /** the server could not be asked; the same token can be tried again */
  | { s: "error"; message: string; p: InvitePreview | null };

interface Coded {
  status?: number;
  code?: string;
  message?: string;
}
const coded = (e: unknown): Coded => (e && typeof e === "object" ? (e as Coded) : {});

/** Is this state a dead end for this token (so it can be forgotten)? */
export const isTerminal = (v: InviteView): boolean =>
  v.s === "used" || v.s === "expired" || v.s === "invalid" || v.s === "apikey";

/** The view for a preview answer. */
export function viewOfPreview(p: InvitePreviewResponse): InviteView {
  if ("state" in p) return { s: p.state };
  if (p.used) return { s: "used" };
  if (p.email_matches !== true) return { s: "mismatch" };
  return { s: "ready", p };
}

/** The view for a failed preview or accept call. `p` is the preview, when there already is one. */
export function viewOfError(e: unknown, p: InvitePreview | null = null): InviteView {
  const { status, code, message } = coded(e);
  if (code === "api_key_not_allowed") return { s: "apikey" };
  if (status === 410 || code === "invitation_expired") return { s: "expired" };
  if (status === 404 || code === "invitation_not_found") return { s: "invalid" };
  if (code === "invitation_used") return { s: "used" };
  if (code === "invitation_email_mismatch") return { s: "mismatch" };
  return { s: "error", message: message ?? "The invitation could not be checked.", p };
}

/** Look at the invitation without accepting it. */
export async function loadInvite(api: Api, token: string): Promise<InviteView> {
  try {
    return viewOfPreview(
      await api.request<InvitePreviewResponse>("POST", "/invitations/preview", { body: { token } }),
    );
  } catch (e) {
    return viewOfError(e);
  }
}

/** Accept it: the new membership, or the view that explains why not. */
export async function acceptInvite(
  api: Api,
  token: string,
  p: InvitePreview,
): Promise<{ ok: true; joined: InviteAccepted } | { ok: false; view: InviteView }> {
  try {
    return {
      ok: true,
      joined: await api.request<InviteAccepted>("POST", "/invitations/accept", { body: { token } }),
    };
  } catch (e) {
    return { ok: false, view: viewOfError(e, p) };
  }
}

export const ROLE_NAME: Record<Role, string> = {
  owner: "Owner",
  producer: "Producer",
  director: "Director",
  maker: "Maker",
  reviewer: "Reviewer",
  viewer: "Viewer",
};

/** What the role lets you do, in the words the Members page uses. */
export const ROLE_BLURB: Record<Role, string> = {
  owner: "Everything, including the workspace itself",
  producer: "Runs briefs, members and costs",
  director: "Builds shots, picks takes, signs off",
  maker: "Renders and uploads takes",
  reviewer: "Comments and reviews",
  viewer: "Looks, nothing else",
};

/** "Sandman invited you" / "You were invited". */
export const inviterLine = (p: Pick<InvitePreview, "invited_by_name">): string =>
  p.invited_by_name ? `${p.invited_by_name} invited you` : "You were invited";

/** "7 days", "tomorrow", "today", "expired", in plain words. */
export function expiresIn(iso: string, now = Date.now()): string {
  const ms = Date.parse(iso) - now;
  if (Number.isNaN(ms)) return "";
  if (ms <= 0) return "has expired";
  const days = Math.ceil(ms / 86_400_000);
  if (days <= 1) return "expires today";
  if (days === 2) return "expires tomorrow";
  return `expires in ${days} days`;
}
